/**
 * `store/resume` — the "last track + position, one tap to continue" offer. `spec.md`
 * §1.4 F13, §13.2, §13.7; `docs/verification-log.md` M6, cell 5.
 *
 * ## Why a slice for one nullable object
 *
 * The reconciliation itself was already built and already tested: `store/persist`'s
 * `load()` reads all four tiers (server, `CloudStorage`, `DeviceStorage`,
 * `localStorage`), takes the newest `at`, and answers a {@link LoadResult}. `Wiring`
 * even exposes the sink for it — `app/wiring.ts`'s `persistence` field carries the
 * comment "so the entry point can `load()` on boot". What was missing was any path
 * from that answer to the screen: `boot()` never called `load()`, and `ResumeBanner`
 * was rendered by nothing but its own test. A reload therefore lost the position **and
 * the track**, which is the cell M6 records as a FAIL.
 *
 * This slice is that path, and it is deliberately the smallest thing that can be one:
 * `boot()` writes the offer once, the crate reads it, a tap or a dismiss clears it.
 * Putting it on `store/player` instead would mix "what is playing" with "what could be
 * played", and every `player` subscriber would re-render for a value only one screen
 * ever reads.
 *
 * ## An offer, never an action
 *
 * §13.2: playback restrictions are cleared per element **by a gesture**, and the reload
 * is not one. So nothing here starts anything. The state carries what to show and the
 * numbers needed to act, and the acting is the banner's `onResume` — reached from a
 * real tap, which is very often the gesture that unlocks audio for the whole session.
 *
 * ## It never invents a title
 *
 * §12.6 forbids fabricated metadata, and only the server tier answers with a `track`
 * payload — the cache tiers know an id, not a name. So {@link ResumeOffer} requires the
 * track, and {@link offerFromLoadResult} returns `null` when there is no name to print
 * rather than a banner reading "Трек 39". A restore that cannot be *described* is
 * simply not offered; the queue is still restored underneath it.
 */

import { create } from 'zustand';

import type { TrackSummary } from '@bl/shared';

import type { LoadResult } from './persist';

/** Everything the banner needs, and nothing it does not. */
export interface ResumeOffer {
  /** The track the pointer named, as `GET /api/playback` described it. */
  readonly track: TrackSummary;
  /** Where to resume from, in milliseconds. `0` when the pointer had no position. */
  readonly positionMs: number;
  /** The queue index to resume at, for `wiring.select(index, …)`. */
  readonly queueIndex: number;
}

export interface ResumeData {
  /** `null` before boot has looked, and after a resume or a dismiss. */
  offer: ResumeOffer | null;
}

export interface ResumeActions {
  /** Publish what `persistence.load()` found. `null` clears any standing offer. */
  setOffer(offer: ResumeOffer | null): void;
  /**
   * Withdraw the offer. Called on both outcomes — resumed and dismissed — because the
   * banner must not survive its own tap, and §8.8 gives a state exactly one action.
   */
  clear(): void;
}

export type ResumeState = ResumeData & ResumeActions;

const EMPTY: ResumeData = { offer: null };

/** A factory, matching `store/session`, so a test can hold an independent slice. */
export function createResumeStore() {
  return create<ResumeState>()((set) => ({
    ...EMPTY,
    setOffer(offer: ResumeOffer | null): void {
      set({ offer });
    },
    clear(): void {
      set({ offer: null });
    },
  }));
}

/** The app-wide resume slice. */
export const useResumeStore = createResumeStore();

/**
 * Turn a {@link LoadResult} into an offer, or `null` when there is nothing worth
 * offering. Pure, so the boot order is the only thing left to get wrong.
 *
 * `null` when:
 *  · no tier answered (`pointer === null`);
 *  · the pointer names no track — §13.7's "a pointer with no track is an empty slot,
 *    not a resume";
 *  · the winning tier carried no `track` payload, so the banner has no title and §12.6
 *    forbids inventing one;
 *  · the pointer's track and the payload disagree, which would print one record's name
 *    over another's position.
 */
export function offerFromLoadResult(result: LoadResult): ResumeOffer | null {
  const pointer = result.pointer;
  if (pointer === null || pointer.t === null) return null;

  const track = result.track;
  if (track === null || track.id !== pointer.t) return null;

  return {
    track,
    positionMs: Math.max(0, pointer.p),
    queueIndex: Math.max(0, pointer.i),
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * Selectors — derived values live here, never in state
 * ──────────────────────────────────────────────────────────────────────────── */

export const selectHasResumeOffer = (state: ResumeData): boolean => state.offer !== null;
